"use client"

import { useEffect, useMemo, useRef } from "react"
import Carousel from "@/components/common/Carousel"
import { useDevicePassedWidth } from "@/hooks/app/useDevicePassedWidth"
import FaqImageCard from "./FaqImageCard"
import FaqInfoCard from "./FaqInfoCard"
import type { FaqDto } from "../types"
import type { FaqDisplayMeta } from "../api/matchMeta"

type FaqCardEntry = {
  faq: FaqDto
  meta: FaqDisplayMeta
}

type FaqCardsRowProps = {
  imageCards: FaqCardEntry[]
  infoCards: FaqCardEntry[]
  locale: string
  onOpen: (faq: FaqDto) => void
}

const IMAGE_CARDS_CAROUSEL_OPTIONS = {
  pageDots: false,
  cellAlign: "left" as const,
  draggable: true,
  prevNextButtons: true,
  contain: true,
  groupCells: true,
}

const INFO_CARDS_CAROUSEL_OPTIONS = {
  pageDots: false,
  cellAlign: "left" as const,
  draggable: true,
  prevNextButtons: true,
  contain: true,
  groupCells: true,
}

/**
 * Cả 2 loại đều chạy Carousel (Flickity, xem src/components/common/Carousel) — không
 * dùng thanh scroll thủ công, không autoPlay (tự chạy liên tục gây rối mắt) — chỉ
 * kéo tay hoặc bấm nút prev/next. INFO_CARD xếp 2 hàng chồng nhau nên tách thành 2
 * carousel độc lập xếp dọc (Flickity không hỗ trợ cell nhiều hàng trong 1 instance).
 */
export default function FaqCardsRow({ imageCards, infoCards, locale, onOpen }: FaqCardsRowProps) {
  const infoRows = useMemo(() => {
    const mid = Math.ceil(infoCards.length / 2)
    return [infoCards.slice(0, mid), infoCards.slice(mid)].filter((row) => row.length > 0)
  }, [infoCards])

  // Flickity đo width lúc container có thể chưa ổn định (SSR streaming/Suspense resolve)
  // khiến groupCells tính sai, để lọt 1 card bị cắt nửa ở rìa thay vì gộp hẳn sang lượt lật
  // sau — resize lại đúng lúc width body đã đo ổn định, giống FlightDealsDesktop.tsx.
  const widthKnown = useDevicePassedWidth((w) => w > 0)
  const flickityInstances = useRef<any[]>([])

  useEffect(() => {
    if (widthKnown) {
      flickityInstances.current.forEach((instance) => instance?.resize())
    }
  }, [widthKnown])

  if (imageCards.length === 0 && infoCards.length === 0) return null

  return (
    <div className="faq-cards">
      {imageCards.length > 0 && (
        <div className={`faq-cards__images${infoCards.length === 0 ? " faq-cards__images--full" : ""}`}>
          <Carousel
            className="carousel-main faq-cards__images-list"
            options={IMAGE_CARDS_CAROUSEL_OPTIONS}
            flickityRef={(flickity) => { flickityInstances.current[0] = flickity }}
          >
            {imageCards.map(({ faq, meta }) => (
              <FaqImageCard
                key={faq.id}
                faq={faq}
                locale={locale}
                image={meta.image}
                description={meta.description}
                onOpen={onOpen}
                className="flickity-container flickity-cell"
              />
            ))}
          </Carousel>
        </div>
      )}

      {infoCards.length > 0 && (
        <div className={`faq-cards__infos${imageCards.length === 0 ? " faq-cards__infos--full" : ""}`}>
          {infoRows.map((row, rowIndex) => (
            <Carousel
              key={rowIndex}
              className="carousel-main faq-cards__infos-row"
              options={INFO_CARDS_CAROUSEL_OPTIONS}
              flickityRef={(flickity) => { flickityInstances.current[rowIndex + 1] = flickity }}
            >
              {row.map(({ faq, meta }) => (
                <FaqInfoCard
                  key={faq.id}
                  faq={faq}
                  locale={locale}
                  image={meta.image}
                  description={meta.description}
                  onOpen={onOpen}
                  className="flickity-container flickity-cell"
                />
              ))}
            </Carousel>
          ))}
        </div>
      )}
    </div>
  )
}
